import { getFragmentData } from 'gql'
import { ErrorFragmentFragmentDoc } from 'gql/graphql'
import { enqueueSnackbar } from 'notistack'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AddEditDomainContext } from 'features/inventory/domain/contexts/AddEditDomainContext'
import { useAbility } from 'providers/casl'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { toDTO } from './api/mapper'
import { UPDATE_DOMAIN_K_WORDS } from './api/mutation'

import type { UseUpdateDomainKWords } from './types'
import type { Schema } from 'features/inventory/domain/forms/RichPlacementsForm/sections/KWordsForm/schema'
import type { UpdateDomainKWordsMutation, UpdateDomainKWordsMutationVariables } from 'gql/graphql'
import type { FieldErrors } from 'react-hook-form'

export const useUpdateDomainKWords = (): UseUpdateDomainKWords => {
  const ability = useAbility()
  const { t } = useTranslation('features/domain', {
    keyPrefix: 'form.richPlacements.kWords.action',
  })
  const { domain } = useContext(AddEditDomainContext)
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  if (domain === undefined) {
    throw Error('Domain should be accessible')
  }

  const { isPending, mutateAsync } = useMutation<
    { input: UpdateDomainKWordsMutationVariables['input'] },
    UpdateDomainKWordsMutation
  >({
    mutation: UPDATE_DOMAIN_K_WORDS,
    onCompleted: async (data: UpdateDomainKWordsMutation): Promise<void> => {
      const apiErrors = data.updateDomainRichPlacementKwordsConfiguration?.errors

      if (apiErrors && apiErrors.length > 0) {
        setErrors(mapGraphQLErrors(getFragmentData(ErrorFragmentFragmentDoc, apiErrors)))
        enqueueSnackbar(t('error'), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('success'), { variant: 'success' })
      }
    },
    queriesToInvalidate: ['domain'],
  })

  const update = (data: Schema): void =>
    void mutateAsync({
      input: {
        ...toDTO(data, ability),
        domainId: domain.id,
      },
    })

  return { errors, isPending, update }
}
